import React, { useState, useEffect } from 'react';
import inspectionStorage from '../services/inspectionStorage';
import syncManager from '../services/syncManager';
import apiService from '../services/apiService';
import pendingPhotosService from '../services/pendingPhotosService';
import ObservationModal from './modals/ObservationModal';
import QuickPhotoButton from './QuickPhotoButton';

function Etape6({ mission, onBack, onSave }) {
  const [formData, setFormData] = useState({
    locaux: [],
    classement_locaux: 'propose',
    zones_explosion: 'sans_objet'
  });

  const [currentLocal, setCurrentLocal] = useState({
    categorie: '',
    designation: '',
    local_data: null
  });

  const [referentielData, setReferentielData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [isLoadingReferentiel, setIsLoadingReferentiel] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [observationContext, setObservationContext] = useState(null);
  const [obsInitialPhotos, setObsInitialPhotos] = useState([]);
  const [obsPendingPhotoId, setObsPendingPhotoId] = useState(null);

  // 🔥 FONCTIONS UTILITAIRES : Nettoyer les valeurs IP/IK pour éviter les doublons
  const cleanIPValue = (value) => {
    if (!value) return '';
    if (value.toString().toUpperCase().startsWith('IP')) return value;
    return `IP${value}`;
  };

  const cleanIKValue = (value) => {
    if (!value) return '';
    if (value.toString().toUpperCase().startsWith('IK')) return value;
    return `IK${value}`;
  };

  useEffect(() => {
    loadSavedData();
    loadReferentiel();
  }, []);

  const loadSavedData = async () => {
    try {
      const saved = await inspectionStorage.getStep(mission.id, 'etape_6_classification');
      if (saved) {
        setFormData({
          locaux: saved.locaux || [],
          classement_locaux: saved.classement_locaux || 'propose',
          zones_explosion: saved.zones_explosion || 'sans_objet'
        });
        console.log('📦 Données Étape 6 récupérées depuis le cache');
      }
    } catch (error) {
      console.error('❌ Erreur chargement cache:', error);
    }
  };

  const loadReferentiel = async () => {
    setIsLoadingReferentiel(true);
    try {
      const cached = await inspectionStorage.get('referentiel_locaux');
      if (cached) {
        setReferentielData(cached);
        const uniqueCategories = [...new Set(cached.map(item => item.categorie))].sort();
        setCategories(uniqueCategories);
        console.log('📦 Référentiel UTE chargé depuis le cache');
        setIsLoadingReferentiel(false);
        return;
      }

      const result = await apiService.getReferentielLocaux();
      if (result.success) {
        setReferentielData(result.referentiels);
        const uniqueCategories = [...new Set(result.referentiels.map(item => item.categorie))].sort();
        setCategories(uniqueCategories);
        await inspectionStorage.set('referentiel_locaux', result.referentiels);
        console.log('✅ Référentiel UTE chargé depuis l\'API');
      }
    } catch (error) {
      console.error('❌ Erreur chargement référentiel:', error);
    } finally {
      setIsLoadingReferentiel(false);
    }
  };

  const saveStepData = async (data) => {
    const stepData = {
      locaux: data.locaux || [],
      classement_locaux: data.classement_locaux || 'propose',
      zones_explosion: data.zones_explosion || 'sans_objet',
      timestamp: new Date().toISOString()
    };
    
    await inspectionStorage.saveStep(mission.id, 'etape_6_classification', stepData);
    syncManager.syncStep(mission.id, 'etape_6_classification', stepData);
  };

  // 🔥 Mettre à jour les désignations quand la catégorie change
  useEffect(() => {
    if (currentLocal.categorie) {
      const filteredDesignations = referentielData
        .filter(item => item.categorie === currentLocal.categorie)
        .map(item => item.designation)
        .sort();
      setDesignations(filteredDesignations);
    } else {
      setDesignations([]);
    }
  }, [currentLocal.categorie, referentielData]);

  const updateCurrentLocal = (field, value) => {
    if (field === 'categorie') {
      setCurrentLocal({
        categorie: value,
        designation: '',
        local_data: null
      });
    } else {
      setCurrentLocal(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const selectDesignation = (designation) => {
    const local = referentielData.find(item => 
      item.categorie === currentLocal.categorie && 
      item.designation === designation
    );
    setCurrentLocal(prev => ({
      ...prev,
      designation: designation,
      local_data: local || null
    }));
  };

  const formatIndicesProtection = (localData) => {
    if (!localData) return '';
    const ipFormatted = cleanIPValue(localData.ip);
    const ikFormatted = cleanIKValue(localData.ik);
    if (ipFormatted && ikFormatted) {
      return `${ipFormatted} - ${ikFormatted}`;
    }
    return '';
  };

  const formatInfluencesExternes = (localData) => {
    if (!localData) return '';
    if (localData.influences) return localData.influences;
    const parts = [];
    if (localData.ae) parts.push(`AE${localData.ae}`);
    if (localData.ad) parts.push(`AD${localData.ad}`);
    if (localData.ag) parts.push(`AG${localData.ag}`);
    if (localData.be) parts.push(`BE${localData.be}`);
    if (localData.af) parts.push(`AF${localData.af}`);
    return parts.join('-');
  };

  const addLocal = async () => {
    if (currentLocal.categorie && currentLocal.designation && currentLocal.local_data) {
      const newLocal = {
        designation: currentLocal.local_data.designation,
        indices_protection: formatIndicesProtection(currentLocal.local_data),
        influences_externes: formatInfluencesExternes(currentLocal.local_data),
        commentaires: currentLocal.local_data.notes || "",
        numero_obs: ""
      };

      const newFormData = {
        ...formData,
        locaux: [...formData.locaux, newLocal]
      };
      
      setFormData(newFormData);
      await saveStepData(newFormData);
      
      setCurrentLocal({
        categorie: '',
        designation: '',
        local_data: null
      });
    }
  };

  const removeLocal = async (index) => {
    const newFormData = {
      ...formData,
      locaux: formData.locaux.filter((_, i) => i !== index)
    };
    setFormData(newFormData);
    await saveStepData(newFormData);
  };

  const updateLocalField = async (index, field, value) => {
    const newLocaux = [...formData.locaux];
    newLocaux[index] = { ...newLocaux[index], [field]: value };
    const newFormData = { ...formData, locaux: newLocaux };
    setFormData(newFormData);
    await saveStepData(newFormData);
  };

  const updateField = async (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    };
    setFormData(newFormData);
    await saveStepData(newFormData);
  };

  const buildLocalContext = (local, index) => ({
    step_origin: 'etape_6_classification_locaux',
    localisation: local.designation,
    element: `Classification local - ${local.designation}`,
    element_ref: {
      type: 'classification_local',
      index: index,
      indices_protection: local.indices_protection,
      influences_externes: local.influences_externes,
    },
  });

  const openObsModalWith = (ctx, initialPhotos = [], pendingPhotoId = null) => {
    setObservationContext(ctx);
    setObsInitialPhotos(initialPhotos);
    setObsPendingPhotoId(pendingPhotoId);
    setShowObservationModal(true);
  };

  const openObservationForLocal = (local, index) => {
    openObsModalWith(buildLocalContext(local, index));
  };

  const handleQuickPhotoOpenObs = (photo, ctx) => {
    if (!photo) return;
    const url = photo.url || photo.blob_base64;
    openObsModalWith(ctx, url ? [url] : [], photo.id);
  };

  const handleCloseObsModal = () => {
    setShowObservationModal(false);
    setObservationContext(null);
    setObsInitialPhotos([]);
    setObsPendingPhotoId(null);
  };

  const handleSaveObservation = async (observationData) => {
    try {
      const etape11Data = await inspectionStorage.getStep(mission.id, 'etape_11_observations') || {};
      const currentObservations = etape11Data.observations || [];
      
      const dernierNumero = currentObservations.length > 0
        ? Math.max(...currentObservations.map(o => o.numero || 0))
        : 0;
      const nouveauNumero = dernierNumero + 1;
      
      const observation = {
        ...observationData,
        numero: nouveauNumero,
        created_date: observationData.created_date || new Date().toISOString(),
        photos: observationData.photos || []
      };
      
      const updatedObservations = [...currentObservations, observation];
      
      const obsStepData = {
        observations: updatedObservations,
        dernier_numero: nouveauNumero,
        timestamp: new Date().toISOString()
      };
      
      await inspectionStorage.saveStep(mission.id, 'etape_11_observations', obsStepData);
      
      // 🔥 AUTO-SYNC observations vers Base44
      syncManager.syncStep(mission.id, 'etape_11_observations', obsStepData);

      if (observationContext?.element_ref?.type === 'classification_local') {
        const localIndex = observationContext.element_ref.index;
        const newLocaux = [...formData.locaux];
        newLocaux[localIndex] = { ...newLocaux[localIndex], numero_obs: nouveauNumero.toString() };
        
        const newFormData = { ...formData, locaux: newLocaux };
        setFormData(newFormData);
        await saveStepData(newFormData);
      }

      console.log('✅ Observation sauvegardée:', nouveauNumero);

      if (obsPendingPhotoId) {
        try {
          await pendingPhotosService.remove(mission.id, obsPendingPhotoId);
        } catch (e) {
          console.warn('[Etape6] suppression pending photo échouée :', e?.message || e);
        }
      }

      alert(`✅ Observation N°${nouveauNumero} créée avec succès !`);
    } catch (error) {
      console.error('❌ Erreur sauvegarde observation:', error);
      alert('Erreur lors de la sauvegarde de l\'observation');
    }

    handleCloseObsModal();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveStepData(formData);
      
      console.log('✅ Étape 6 (classification) sauvegardée localement');
      
      if (onSave) {
        onSave(formData);
      }

      alert('✅ Étape 6 sauvegardée avec succès !');
      
      onBack();
      
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="etape-container">
      <div className="etape-header">
        <button className="back-button" onClick={onBack}>
          ← Retour
        </button>
        <div className="etape-title">
          <h1>Étape 6/14</h1>
          <p>Classification des locaux</p>
        </div>
        <div></div>
      </div>

      <div className="etape-content">
        
        <div className="form-section classification-form">
          <h2>🔍 Ajouter un local depuis le référentiel UTE C15-103</h2>
          
          {isLoadingReferentiel ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Chargement du référentiel UTE C15-103...</p>
            </div>
          ) : (
            <>
              <div className="info-grid-2">
                <div className="form-group">
                  <label className="required">1. Catégorie</label>
                  <select
                    value={currentLocal.categorie}
                    onChange={(e) => updateCurrentLocal('categorie', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Sélectionner...</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="required">2. Désignation du local</label>
                  <select
                    value={currentLocal.designation}
                    onChange={(e) => selectDesignation(e.target.value)}
                    className="form-select"
                    disabled={!currentLocal.categorie}
                  >
                    <option value="">Sélectionner...</option>
                    {designations.map((designation, idx) => (
                      <option key={idx} value={designation}>{designation}</option>
                    ))}
                  </select>
                </div>
              </div>

              {currentLocal.local_data && (
                <div className="selected-local-preview">
                  <p><strong>Indices protection:</strong> {formatIndicesProtection(currentLocal.local_data)}</p>
                  <p><strong>Influences externes:</strong> {formatInfluencesExternes(currentLocal.local_data)}</p>
                  {currentLocal.local_data.notes && <p className="text-xs italic">Note: {currentLocal.local_data.notes}</p>}
                </div>
              )}

              <div className="form-group">
                <label>Saisie manuelle</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Ou saisir manuellement la désignation du local..."
                  onKeyPress={async (e) => {
                    if (e.key === 'Enter' && e.target.value) {
                      const newLocal = {
                        designation: e.target.value,
                        indices_protection: '',
                        influences_externes: '',
                        commentaires: '',
                        numero_obs: ''
                      };
                      const newFormData = {
                        ...formData,
                        locaux: [...formData.locaux, newLocal]
                      };
                      setFormData(newFormData);
                      await saveStepData(newFormData);
                      e.target.value = '';
                    }
                  }}
                />
                <p className="helper-text">Appuyez sur Entrée pour ajouter</p>
              </div>

              <button
                onClick={addLocal}
                className="btn btn-primary"
                disabled={!currentLocal.categorie || !currentLocal.designation}
              >
                ➕ Ajouter le local à la liste
              </button>
            </>
          )}
        </div>

        <div className="form-section locaux-list">
          <h2>🏢 Liste des locaux et emplacements classifiés ({formData.locaux.length})</h2>
          
          {formData.locaux.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🏢</div>
              <p>Aucun local ajouté pour le moment.</p>
            </div>
          ) : (
            <div className="locaux-table-container">
              <table className="locaux-table">
                <thead>
                  <tr>
                    <th>Désignation</th>
                    <th>Indices protection</th>
                    <th>Influences externes</th>
                    <th>Commentaires</th>
                    <th>N° OBS</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.locaux.map((local, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          value={local.designation || ''}
                          onChange={(e) => updateLocalField(index, 'designation', e.target.value)}
                          className="table-input"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={local.indices_protection || ''}
                          onChange={(e) => updateLocalField(index, 'indices_protection', e.target.value)}
                          className="table-input mono"
                          placeholder="IP24 - IK07"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={local.influences_externes || ''}
                          onChange={(e) => updateLocalField(index, 'influences_externes', e.target.value)}
                          className="table-input mono"
                          placeholder="AE1-AD4-AG2"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={local.commentaires || ''}
                          onChange={(e) => updateLocalField(index, 'commentaires', e.target.value)}
                          className="table-input"
                          placeholder="-"
                        />
                      </td>
                      <td className="obs-cell">
                        {local.numero_obs || '-'}
                      </td>
                      <td className="actions-cell">
                        <button
                          onClick={() => openObservationForLocal(local, index)}
                          className="btn-action btn-obs"
                          title="Ajouter une observation"
                        >
                          ⚠️
                        </button>
                        <QuickPhotoButton
                          missionId={mission?.id}
                          context={buildLocalContext(local, index)}
                          onOpenObservation={handleQuickPhotoOpenObs}
                          title="Prendre une photo du local"
                        />
                        <button
                          onClick={() => removeLocal(index)}
                          className="btn-action btn-delete"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="form-section options-section">
          <h2>⚙️ Options de classement</h2>
          
          <div className="option-group">
            <h3>Classement des locaux :</h3>
            <label className="radio-option">
              <input
                type="radio"
                name="classement_locaux"
                value="propose"
                checked={formData.classement_locaux === 'propose'}
                onChange={(e) => updateField('classement_locaux', e.target.value)}
              />
              <span>Proposé par le vérificateur selon le guide UTE C 15-103 (à valider par le chef d'établissement)</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="classement_locaux"
                value="communique"
                checked={formData.classement_locaux === 'communique'}
                onChange={(e) => updateField('classement_locaux', e.target.value)}
              />
              <span>Communiqué par le chef d'établissement</span>
            </label>
          </div>

          <div className="option-group">
            <h3>Classement des zones à risques d'explosion (DRPCE) :</h3>
            <div className="radio-inline">
              <label className="radio-option-inline">
                <input
                  type="radio"
                  name="zones_explosion"
                  value="oui"
                  checked={formData.zones_explosion === 'oui'}
                  onChange={(e) => updateField('zones_explosion', e.target.value)}
                />
                <span>OUI</span>
              </label>
              <label className="radio-option-inline">
                <input
                  type="radio"
                  name="zones_explosion"
                  value="non"
                  checked={formData.zones_explosion === 'non'}
                  onChange={(e) => updateField('zones_explosion', e.target.value)}
                />
                <span>NON</span>
              </label>
              <label className="radio-option-inline">
                <input
                  type="radio"
                  name="zones_explosion"
                  value="sans_objet"
                  checked={formData.zones_explosion === 'sans_objet'}
                  onChange={(e) => updateField('zones_explosion', e.target.value)}
                />
                <span>SANS OBJET</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="etape-actions">
        <button 
          className="btn btn-secondary" 
          onClick={onBack}
          disabled={isSaving}
        >
          Annuler
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? '💾 Sauvegarde...' : '💾 Sauvegarder et continuer'}
        </button>
      </div>

      {showObservationModal && (
        <ObservationModal
          isOpen={showObservationModal}
          onClose={handleCloseObsModal}
          onSave={handleSaveObservation}
          context={observationContext}
          initialPhotos={obsInitialPhotos}
          missionId={mission?.id}
        />
      )}
    </div>
  );
}

export default Etape6;